import { Coordinate } from "ol/coordinate";
import { Extent, getCenter as getExtentCenter, getForViewAndSize, getSize as getExtentSize, scaleFromCenter } from "ol/extent";
import { addProjection, get as getProjection, Projection, ProjectionLike } from "ol/proj";
import { Options as ProjectionOptions } from "ol/proj/Projection";
import { Size } from "ol/size";

// #region MapBox
//TODO: move this token into the common config
export const accessToken =
  'pk.eyJ1IjoidGVycmFnby1hY2NvdW50IiwiYSI6ImNrZm4wdGN3ZDFreXAycm8wbjg4amV1dzQifQ.36pfqYSU_1ChaGGRM4i1mQ';
// #endregion

// #region OpenLayers
export const MERCATOR_SRS_ID = 'EPSG:4326' as const;  // TODO: [S5-3775] Convert to enum?

export const getCenteredExtentForSize = (size: Size, center: Coordinate = [0, 0], scaleFactor?: number): Extent => {
  const extent = getForViewAndSize(center, 1, 0, size);
  if (scaleFactor) {
    scaleFromCenter(extent, scaleFactor);
  }
  return extent;
}

export const getFitExtentForSize = (size: Size, extent: Extent): Extent => {
  const [width, height] = size;
  const [dx, dy] = getExtentSize(extent);
  const [cx, cy] = getExtentCenter(extent);
  const [xRatio, yRatio] = [width / dx, height / dy];

  // return getCenteredExtentForSize(size, [cx, cy], 1 / Math.max(xRatio, yRatio));

  const [newWidth, newHeight] = xRatio > yRatio ? [dx, height / xRatio] : [width / yRatio, dy];
  return [cx - newWidth / 2, cy - newHeight / 2, cx + newWidth / 2, cy + newHeight / 2];
}

export const getFitExtentForSizeFromProjection = <T extends ProjectionLike>(size: Size, projectionLike: T) => {
  const projection = getProjection(projectionLike);
  const extent = projection?.getWorldExtent() ?? projection?.getExtent();
  return (extent && getFitExtentForSize(size, extent)) as T extends Projection ? Extent : Extent | undefined;
}

export const getOptionsFromProjection = (proj: Projection): ProjectionOptions => ({
  code: proj.getCode(),
  axisOrientation: proj.getAxisOrientation(),
  extent: proj.getExtent(),
  // @ts-ignore: Typedef for `getPointResolutionFunc` has incorrect return type grouping.
  getPointResolution: proj.getPointResolutionFunc(),
  global: proj.isGlobal(),
  metersPerUnit: proj.getMetersPerUnit(),
  units: proj.getUnits(),
  worldExtent: proj.getWorldExtent(),
});

export const cloneProjection = (proj: Projection, options?: ProjectionOptions): Projection => new Projection({
  ...getOptionsFromProjection(proj),
  ...options,
});

export const getFitProjectionForSize = (size: Size, extent: Extent, options: ProjectionOptions, addToCache?: boolean): Projection => {
  const newProj = new Projection({ worldExtent: extent, extent: getFitExtentForSize(size, extent), ...options });
  addToCache && addProjection(newProj);
  return newProj;
};

export const getFitProjectionForSizeFromProjection = (size: Size, proj: Projection, options: ProjectionOptions, addToCache?: boolean): Projection => {
  const newProj = cloneProjection(proj, { extent: getFitExtentForSizeFromProjection(size, proj), ...options });
  addToCache && addProjection(newProj);
  return newProj;
};

export const getMercatorProjectionForSize = (size: Size, options: ProjectionOptions, addToCache?: boolean): Projection => {
  return getFitProjectionForSizeFromProjection(size, getProjection(MERCATOR_SRS_ID)!, options, addToCache);
};
// #endregion
